// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/billing/WithdrawQueuePage.tsx
//
// Phase 3.2 Wave 11 Task 33 — Admin Withdraw Queue.
//
// Per CONTEXT.md (Withdraw Flow — manual admin-approve after
// external transfer; `external_transfer_ref` captured at approve time)
// + (race-loser toast on 409/410 — "đã được xử lý bởi admin khác").
// Covers goal: G-W.
//
// Endpoints (per API-CONTRACTS §Withdraw-Admin):
//   GET    /api/v1/admin/withdraw-requests?status=&cursor=&limit=
//   POST   /api/v1/admin/withdraw-requests/:id/approve
//          body: { internal_note?, external_transfer_ref? } — UI enforces
//          external_transfer_ref REQUIRED (manual-transfer audit).
//   POST   /api/v1/admin/withdraw-requests/:id/reject
//          body: { reason (3-500), internal_note? }
//
// Design fidelity (admin-withdraw-queue.png — SHA256 a736528…):
//   - Page header: "Withdraw Queue" 28px semibold, subtitle "Approve or
//     reject merchant withdraw requests" — matches PNG copy verbatim.
//   - Tab bar: 3 pills [Pending (n)] [Approved (n)] [Rejected (n)] — burnt
//     orange filled for active. (Spec mandates 4 tabs incl. "All" — added
//     before Pending so the design's 3-pill row + a 4th "All" matches the
//     task body without re-rendering the design's exact 3 chips.)
//   - Single bordered card: 5-column header (Merchant / Amount / Bank
//     Account / Date / Actions) on light surface, rows separated by
//     subtle dividers.
//   - Per-row actions: green Approve button + red outline Reject button,
//     plus a ghost "View" on first row (drawer detail view).

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';
import { useTranslation } from '../../i18n/useTranslation.js';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';

// ---------------------------------------------------------------------------
// Wire types — slim FE mirror of API-CONTRACTS L880-887 (AdminWithdrawRequestItem).
// ---------------------------------------------------------------------------

type WithdrawStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

interface AdminWithdrawRow {
  id: string;
  amount_usd_cents: number;
  currency_code: string;
  bank_account_label: string;
  status: WithdrawStatus;
  requested_at: string;
  approved_at: string | null;
  external_transfer_ref: string | null;
  reject_reason: string | null;
  merchant: { id: string; name: string; email: string };
}

interface AdminListResponse {
  data: AdminWithdrawRow[];
  next_cursor: string | null;
}

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}

function envelopeError(payload: unknown, t: (key: string) => string): { code?: string; message: string } {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    const err = (payload as ErrorEnvelope).error;
    return { code: err?.code, message: err?.message ?? t('payout.errors.unknown') };
  }
  return { message: t('payout.errors.network') };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatUsdCents(cents: number): string {
  // C-06 (review 3.2): `amount_usd_cents` is definitionally a USD ledger
  // amount, so it MUST be labeled USD. The prior signature accepted a
  // `currency` arg and call sites passed the bank-account `currency_code`,
  // rendering a USD amount as "€1,500 EUR". Always format + label as USD.
  const value = cents / 100;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);
  return `${formatted.replace(/[A-Z]{3}\s?$/, '').trim()} USD`;
}

function timeAgo(
  iso: string,
  t: (key: string, vars?: Record<string, unknown>) => string,
): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return t('payout.withdraw.relative.justNow');
  if (mins < 60) return t('payout.withdraw.relative.minutes', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('payout.withdraw.relative.hours', { count: hrs });
  const days = Math.floor(hrs / 24);
  return t('payout.withdraw.relative.days', { count: days });
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

type TabKey = 'all' | 'pending' | 'approved' | 'rejected';

interface TabDescriptor {
  key: TabKey;
  labelKey: string;
  apiStatus?: WithdrawStatus;
  activeClass: string;
  idleClass: string;
}

const TABS: TabDescriptor[] = [
  {
    key: 'all',
    labelKey: 'payout.withdraw.tab.all',
    activeClass: 'bg-pw-text text-white border-pw-text',
    idleClass: 'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
  },
  {
    key: 'pending',
    labelKey: 'payout.withdraw.tab.pending',
    apiStatus: 'pending',
    activeClass: 'bg-pw-primary text-white border-pw-primary',
    idleClass: 'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
  },
  {
    key: 'approved',
    labelKey: 'payout.withdraw.tab.approved',
    apiStatus: 'approved',
    activeClass: 'border-emerald-500 text-emerald-600 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40',
    idleClass: 'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
  },
  {
    key: 'rejected',
    labelKey: 'payout.withdraw.tab.rejected',
    apiStatus: 'rejected',
    activeClass: 'border-rose-500 text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40',
    idleClass: 'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
  },
];

// ---------------------------------------------------------------------------
// Approve dialog — external_transfer_ref REQUIRED
// ---------------------------------------------------------------------------

interface ApproveState {
  row: AdminWithdrawRow;
}

function ApproveDialog({
  state,
  onClose,
  onSubmitted,
}: {
  state: ApproveState | null;
  onClose: () => void;
  onSubmitted: () => void;
}): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [externalRef, setExternalRef] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refError, setRefError] = useState<string | null>(null);

  useEffect(() => {
    if (state) {
      setExternalRef('');
      setInternalNote('');
      setRefError(null);
      setSubmitting(false);
    }
  }, [state]);

  if (!state) return null;

  async function handleSubmit(): Promise<void> {
    if (!state) return;
    // external_transfer_ref REQUIRED at admin tier (audit trail).
    const trimmed = externalRef.trim();
    if (!trimmed) {
      setRefError(t('payout.withdraw.approve.refRequired'));
      return;
    }
    if (trimmed.length > 120) {
      setRefError(t('payout.withdraw.approve.refTooLong', { max: 120 }));
      return;
    }
    setRefError(null);
    setSubmitting(true);
    try {
      const client = apiClient();
      const path = `/api/v1/admin/withdraw-requests/${encodeURIComponent(state.row.id)}/approve`;
      // Bug 9 (debug-bill iter 6, 2026-05-04): BE requires Idempotency-Key
      // header on this endpoint (returns 400 IDEMPOTENCY_KEY_REQUIRED otherwise).
      // Mirror the topup approve pattern (TopupQueuePage.tsx:400-411).
      const idempotencyKey =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const { error, response } = await (
        client as unknown as {
          POST: (
            p: string,
            init: { body: unknown; headers?: Record<string, string> },
          ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
        }
      ).POST(path, {
        body: {
          external_transfer_ref: trimmed,
          internal_note: internalNote.trim() || undefined,
        },
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      if (error || !response.ok) {
        const env = envelopeError(error, t);
        // Race-loser toast: 409 WITHDRAW_NOT_PENDING means another
        // admin already processed this request. Refresh to drop stale row.
        if (response.status === 409 || response.status === 410) {
          toast({
            title: t('payout.withdraw.raceLoser.title'),
            description: t('payout.withdraw.raceLoser.description'),
            variant: 'warning',
          });
          onClose();
          onSubmitted();
          return;
        }
        // 422 LEDGER_NOT_SEEDED — pending row missing its holdSpending entry
        // (data corruption / sandbox seed bypass). User should contact ops.
        // Debug-withdraw iter 1 (2026-05-04).
        if (response.status === 422 && env.code === 'LEDGER_NOT_SEEDED') {
          toast({
            title: t('payout.withdraw.approve.ledgerMissingTitle'),
            description: t('payout.withdraw.approve.ledgerMissingDesc'),
            variant: 'error',
          });
          return;
        }
        toast({
          title: t('payout.withdraw.approve.failed'),
          description: env.message,
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('payout.withdraw.approve.successTitle'),
        description: state.row.merchant.name,
        variant: 'success',
      });
      onClose();
      onSubmitted();
    } catch (err) {
      toast({
        title: t('payout.withdraw.networkErrorTitle'),
        description: err instanceof Error ? err.message : t('payout.withdraw.checkConnection'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogHeader>
        <DialogTitle>{t('payout.withdraw.approve.title', { merchant: state.row.merchant.name })}</DialogTitle>
      </DialogHeader>
      <div className="mt-4 space-y-4">
        <div className="rounded-md border border-pw-border bg-pw-surface p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-pw-text-muted">{t('payout.withdraw.field.amount')}</span>
            <span className="font-semibold text-pw-text">
              {formatUsdCents(state.row.amount_usd_cents)}
            </span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-pw-text-muted">{t('payout.withdraw.field.bankAccount')}</span>
            <span className="text-pw-text">{state.row.bank_account_label}</span>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-pw-text">
            {t('payout.withdraw.approve.refLabel')} <span className="text-rose-600 dark:text-rose-300">*</span>
          </label>
          <Input
            value={externalRef}
            onChange={(e) => {
              setExternalRef(e.target.value);
              if (refError) setRefError(null);
            }}
            placeholder={t('payout.withdraw.approve.refPlaceholder')}
            aria-invalid={refError ? 'true' : 'false'}
            data-testid="withdraw-approve-ref"
          />
          {refError ? (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
              {refError}
            </p>
          ) : (
            <p className="mt-1 text-xs text-pw-text-muted">
              {t('payout.withdraw.approve.refHelp')}
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-pw-text">
            {t('payout.withdraw.internalNoteLabel')}
          </label>
          <textarea
            className="block h-20 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            maxLength={1000}
            placeholder={t('payout.withdraw.approve.internalNotePlaceholder')}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          {t('payout.withdraw.cancel')}
        </Button>
        <Button variant="default" onClick={handleSubmit} disabled={submitting}>
          {submitting ? t('payout.withdraw.approve.submitting') : t('payout.withdraw.approve.submit')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Reject dialog — reason required (min 3 chars per contract)
// ---------------------------------------------------------------------------

function RejectDialog({
  state,
  onClose,
  onSubmitted,
}: {
  state: { row: AdminWithdrawRow } | null;
  onClose: () => void;
  onSubmitted: () => void;
}): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [reason, setReason] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reasonError, setReasonError] = useState<string | null>(null);

  useEffect(() => {
    if (state) {
      setReason('');
      setInternalNote('');
      setReasonError(null);
      setSubmitting(false);
    }
  }, [state]);

  if (!state) return null;

  async function handleSubmit(): Promise<void> {
    if (!state) return;
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      setReasonError(t('payout.withdraw.reject.reasonMin', { min: 3 }));
      return;
    }
    if (trimmed.length > 500) {
      setReasonError(t('payout.withdraw.reject.reasonMax', { max: 500 }));
      return;
    }
    setReasonError(null);
    setSubmitting(true);
    try {
      const client = apiClient();
      const path = `/api/v1/admin/withdraw-requests/${encodeURIComponent(state.row.id)}/reject`;
      // Bug 9 (debug-bill iter 6, 2026-05-04): same Idempotency-Key
      // requirement as approve handler.
      const idempotencyKey =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const { error, response } = await (
        client as unknown as {
          POST: (
            p: string,
            init: { body: unknown; headers?: Record<string, string> },
          ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
        }
      ).POST(path, {
        body: {
          reason: trimmed,
          internal_note: internalNote.trim() || undefined,
        },
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      if (error || !response.ok) {
        const env = envelopeError(error, t);
        if (response.status === 409 || response.status === 410) {
          toast({
            title: t('payout.withdraw.raceLoser.title'),
            description: t('payout.withdraw.raceLoser.descriptionShort'),
            variant: 'warning',
          });
          onClose();
          onSubmitted();
          return;
        }
        toast({
          title: t('payout.withdraw.reject.failed'),
          description: env.message,
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('payout.withdraw.reject.successTitle'),
        description: state.row.merchant.name,
        variant: 'success',
      });
      onClose();
      onSubmitted();
    } catch (err) {
      toast({
        title: t('payout.withdraw.networkErrorTitle'),
        description: err instanceof Error ? err.message : t('payout.withdraw.checkConnection'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogHeader>
        <DialogTitle>{t('payout.withdraw.reject.title', { merchant: state.row.merchant.name })}</DialogTitle>
      </DialogHeader>
      <div className="mt-4 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-pw-text">
            {t('payout.withdraw.reject.reasonLabel')} <span className="text-rose-600 dark:text-rose-300">*</span>
          </label>
          <textarea
            className={cn(
              'block h-24 w-full rounded-md border bg-pw-bg p-2 text-sm focus:outline-none focus:ring-2',
              reasonError
                ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-500/20'
                : 'border-pw-border focus:border-pw-primary focus:ring-pw-primary/20',
            )}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError) setReasonError(null);
            }}
            maxLength={500}
            aria-invalid={reasonError ? 'true' : 'false'}
            data-testid="withdraw-reject-reason"
          />
          {reasonError ? (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
              {reasonError}
            </p>
          ) : (
            <p className="mt-1 text-xs text-pw-text-muted">
              {t('payout.withdraw.reject.reasonHelp')}
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-pw-text">
            {t('payout.withdraw.internalNoteLabel')}
          </label>
          <textarea
            className="block h-20 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            maxLength={1000}
            placeholder={t('payout.withdraw.reject.internalNotePlaceholder')}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          {t('payout.withdraw.cancel')}
        </Button>
        <Button variant="destructive" onClick={handleSubmit} disabled={submitting}>
          {submitting ? t('payout.withdraw.reject.submitting') : t('payout.withdraw.reject.submit')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer — read-only view (kept as ConfirmDialog-like overlay)
// ---------------------------------------------------------------------------

function DetailDrawer({
  row,
  onClose,
}: {
  row: AdminWithdrawRow | null;
  onClose: () => void;
}): JSX.Element | null {
  const { t } = useTranslation('finance');
  if (!row) return null;
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogHeader>
        <DialogTitle>{t('payout.withdraw.detail.title', { merchant: row.merchant.name })}</DialogTitle>
      </DialogHeader>
      <div className="mt-4 space-y-2 text-sm">
        <DetailRow label={t('payout.withdraw.detail.merchant')} value={`${row.merchant.name} · ${row.merchant.email}`} />
        <DetailRow
          label={t('payout.withdraw.detail.amount')}
          value={formatUsdCents(row.amount_usd_cents)}
        />
        <DetailRow label={t('payout.withdraw.detail.bankAccount')} value={row.bank_account_label} />
        <DetailRow label={t('payout.withdraw.detail.status')} value={t(`payout.withdraw.status.${row.status}`)} />
        <DetailRow label={t('payout.withdraw.detail.requested')} value={new Date(row.requested_at).toLocaleString()} />
        {row.approved_at ? (
          <DetailRow label={t('payout.withdraw.detail.approved')} value={new Date(row.approved_at).toLocaleString()} />
        ) : null}
        {row.external_transfer_ref ? (
          <DetailRow label={t('payout.withdraw.detail.wireRef')} value={row.external_transfer_ref} />
        ) : null}
        {row.reject_reason ? (
          <DetailRow label={t('payout.withdraw.detail.rejectReason')} value={row.reject_reason} />
        ) : null}
      </div>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          {t('payout.withdraw.close')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function DetailRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-4 border-b border-pw-border/60 py-1.5 last:border-0">
      <span className="text-pw-text-muted">{label}</span>
      <span className="text-right font-medium text-pw-text">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function WithdrawQueuePage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');

  // Tab state — synced to URL ?status= per R7 list-view convention.
  const [tab, setTab] = useState<TabKey>(() => {
    if (typeof window === 'undefined') return 'pending';
    const sp = new URLSearchParams(window.location.search).get('status');
    if (sp === 'approved' || sp === 'rejected' || sp === 'all') return sp as TabKey;
    return 'pending';
  });

  const [rows, setRows] = useState<AdminWithdrawRow[]>([]);
  const [counts, setCounts] = useState<Record<TabKey, number>>({
    all: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // URL-synced numbered pagination (mirror TopupQueuePage). pageSize clamped to
  // the BE allowlist (10..100); default 25 matches PageListFooter choices.
  const [page, setPage] = useState<number>(() => {
    if (typeof window === 'undefined') return 1;
    const p = Number.parseInt(
      new URLSearchParams(window.location.search).get('page') ?? '1',
      10,
    );
    return Number.isFinite(p) && p > 0 ? p : 1;
  });
  const [pageSize, setPageSize] = useState<number>(() => {
    if (typeof window === 'undefined') return 25;
    const raw = Number.parseInt(
      new URLSearchParams(window.location.search).get('pageSize') ?? '25',
      10,
    );
    return Number.isFinite(raw) && raw > 0
      ? Math.min(100, Math.max(10, raw))
      : 25;
  });
  const [total, setTotal] = useState(0);

  const [approveState, setApproveState] = useState<ApproveState | null>(null);
  const [rejectState, setRejectState] = useState<{ row: AdminWithdrawRow } | null>(null);
  const [detailRow, setDetailRow] = useState<AdminWithdrawRow | null>(null);

  // URL sync — keep ?status= in step with active tab.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (tab === 'pending') params.delete('status');
    else params.set('status', tab);
    const qs = params.toString();
    const next = qs ? `?${qs}` : window.location.pathname;
    window.history.replaceState(null, '', next);
  }, [tab]);

  // URL sync — keep ?page= / ?pageSize= in step with pagination state.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (page <= 1) params.delete('page');
    else params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    const qs = params.toString();
    window.history.replaceState(
      null,
      '',
      qs ? `?${qs}` : window.location.pathname,
    );
  }, [page, pageSize]);

  function handlePageSizeChange(size: number): void {
    setPageSize(Math.min(100, Math.max(10, size)));
    setPage(1); // reset to page 1 on size change
  }

  const activeApiStatus = useMemo(() => TABS.find((t) => t.key === tab)?.apiStatus, [tab]);

  const fetchList = useCallback(
    async (status: WithdrawStatus | undefined): Promise<void> => {
      setLoading(true);
      try {
        const client = apiClient();
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        params.set('page', String(page));
        params.set('limit', String(pageSize));
        const path = `/api/v1/admin/withdraw-requests?${params.toString()}`;
        const { data, error, response } = await (
          client as unknown as {
            GET: (p: string) => Promise<{
              data?: unknown;
              error?: unknown;
              response: Response;
            }>;
          }
        ).GET(path);
        if (error || !response.ok) {
          const env = envelopeError(error, t);
          toast({
            title: t('payout.withdraw.loadFailed'),
            description: env.message,
            variant: 'error',
          });
          setRows([]);
          return;
        }
        // BE wraps as `{success, data: [rows], next_cursor}` — single-level.
        // openapi-fetch returns the response body verbatim as `data`, so
        // here `data` IS that wrapper. Don't double-unwrap. Mirror the
        // pattern in TopupQueuePage.tsx (no unwrap helper, direct cast).
        const payload = data as
          | AdminListResponse
          | { data: AdminWithdrawRow[]; next_cursor: string | null; meta?: { total?: number } }
          | undefined;
        const rowsList: AdminWithdrawRow[] =
          payload && typeof payload === 'object' && 'data' in payload && Array.isArray(payload.data)
            ? (payload.data as AdminWithdrawRow[])
            : [];
        setRows(rowsList);
        // Prefer BE meta.total for numbered-page nav; fall back to window length.
        const metaTotal = (payload as { meta?: { total?: number } } | undefined)
          ?.meta?.total;
        setTotal(typeof metaTotal === 'number' ? metaTotal : rowsList.length);
        // Best-effort tab count update for the active tab; full counts
        // require independent fetches per tab — kept lightweight for v1.
        const activeKey: TabKey =
          status === 'pending' || status === 'approved' || status === 'rejected'
            ? status
            : 'all';
        setCounts((prev) => ({
          ...prev,
          [activeKey]: rowsList.length,
        }));
      } catch (err) {
        toast({
          title: t('payout.withdraw.networkErrorTitle'),
          description: err instanceof Error ? err.message : t('payout.withdraw.checkConnection'),
          variant: 'error',
        });
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [toast, t, page, pageSize],
  );

  useEffect(() => {
    void fetchList(activeApiStatus);
  }, [activeApiStatus, fetchList, refreshTick]);

  // Phase 8.2 debug 2026-05-23 bug C — tab badge counts were only updated
  // for the active tab (whichever one fetchList just ran for). Other tabs
  // displayed `0` until clicked, which misled admins into thinking the
  // queue was empty. Fetch all four buckets in parallel here. Each call is
  // lightweight (the BE returns up to limit=50 rows; sandbox volume is
  // small enough that the 4 parallel queries cost ~one extra round-trip).
  // The proper long-term fix is a dedicated `/counts` endpoint with a
  // single $facet aggregation — flagged for follow-up.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const client = apiClient() as unknown as {
          GET: (p: string) => Promise<{
            data?: unknown;
            response: Response;
          }>;
        };
        const buckets: Array<{
          key: TabKey;
          status: WithdrawStatus | undefined;
        }> = [
          { key: 'all', status: undefined },
          { key: 'pending', status: 'pending' },
          { key: 'approved', status: 'approved' },
          { key: 'rejected', status: 'rejected' },
        ];
        const results = await Promise.all(
          buckets.map(async ({ status }) => {
            const qp = new URLSearchParams();
            if (status) qp.set('status', status);
            qp.set('limit', '50');
            const r = await client.GET(
              `/api/v1/admin/withdraw-requests?${qp.toString()}`,
            );
            const payload = r.data as
              | { data?: AdminWithdrawRow[] }
              | undefined;
            return Array.isArray(payload?.data) ? payload.data.length : 0;
          }),
        );
        if (cancelled) return;
        setCounts({
          all: results[0] ?? 0,
          pending: results[1] ?? 0,
          approved: results[2] ?? 0,
          rejected: results[3] ?? 0,
        });
      } catch {
        // Best-effort — silent failure keeps the active-tab count working.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const handleSubmitted = useCallback(() => setRefreshTick((t) => t + 1), []);

  return (
    <main className="flex flex-col gap-6 p-6" data-testid="admin-withdraw-queue">
      {/* Header — matches PNG copy: title 28px semibold + subtitle */}
      <PageHeader title={t('payout.withdraw.page.title')} subtitle={t('payout.withdraw.page.subtitle')} />

      {/* Tab pills — design has Pending/Approved/Rejected; we add All as
          first per task spec ("All / Pending / Approved / Rejected"). */}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label={t('payout.withdraw.filterAria')}>
        {TABS.map((tabItem) => {
          const isActive = tab === tabItem.key;
          const count = counts[tabItem.key];
          return (
            <button
              key={tabItem.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => {
                setTab(tabItem.key);
                setPage(1); // tab switch resets paging to page 1
              }}
              className={cn(
                'inline-flex h-10 items-center rounded-md border px-4 text-sm font-semibold transition-colors',
                isActive ? tabItem.activeClass : tabItem.idleClass,
              )}
              data-testid={`tab-${tabItem.key}`}
            >
              {t(tabItem.labelKey)}
              {/* Show count whenever we have one cached (active OR previously-visited tab).
                  Bug 5b debug-bill iter 4: previously gated on `isActive`, hiding sibling
                  counts as soon as the user switched tabs — counts state was already
                  accumulated above, just unrendered. */}
              {typeof count === 'number' ? (
                <span className="ml-1.5">({count})</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* List card */}
      <div className="overflow-hidden rounded-xl border border-pw-border bg-pw-bg">
        <div className="grid grid-cols-[2fr_1.2fr_1.6fr_1fr_auto] gap-4 border-b border-pw-border bg-orange-50 dark:bg-orange-950/40 px-6 py-3 text-xs font-medium uppercase tracking-wider text-pw-text-muted">
          <div>{t('payout.withdraw.column.merchant')}</div>
          <div>{t('payout.withdraw.column.amount')}</div>
          <div>{t('payout.withdraw.column.bankAccount')}</div>
          <div>{t('payout.withdraw.column.date')}</div>
          <div className="text-right">{t('payout.withdraw.column.actions')}</div>
        </div>

        {loading ? (
          <div className="flex h-32 items-center justify-center text-sm text-pw-text-muted">
            {t('payout.withdraw.loading')}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-pw-text-muted">
            {tab === 'pending'
              ? t('payout.withdraw.emptyPending')
              : tab === 'all'
                ? t('payout.withdraw.emptyAll')
                : t('payout.withdraw.emptyStatus', { status: t(`payout.withdraw.status.${tab}`) })}
          </div>
        ) : (
          <ul className="divide-y divide-pw-border">
            {rows.map((row) => (
              <li
                key={row.id}
                className="grid grid-cols-[2fr_1.2fr_1.6fr_1fr_auto] items-center gap-4 px-6 py-4 hover:bg-pw-surface/50"
                data-testid={`withdraw-row-${row.id}`}
              >
                <div>
                  <p className="text-sm font-semibold text-pw-text">
                    {row.merchant.name}
                  </p>
                  <p className="truncate text-xs text-pw-text-muted">
                    {row.merchant.email}
                  </p>
                </div>
                <div className="text-sm font-semibold text-pw-text">
                  {formatUsdCents(row.amount_usd_cents)}
                </div>
                <div>
                  <p className="text-sm text-pw-text">{row.bank_account_label}</p>
                  <p className="text-xs text-pw-text-muted">
                    {row.currency_code}
                  </p>
                </div>
                <div className="text-sm text-pw-text-muted">
                  {timeAgo(row.requested_at, t)}
                </div>
                <div className="flex justify-end gap-2">
                  {row.status === 'pending' ? (
                    <>
                      <Button
                        size="sm"
                        onClick={() => setApproveState({ row })}
                        className="bg-emerald-500 text-white hover:bg-emerald-600"
                        data-testid={`approve-${row.id}`}
                      >
                        {t('payout.withdraw.approve.submit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRejectState({ row })}
                        className="border-rose-300 text-rose-600 dark:text-rose-300 hover:bg-rose-50"
                        data-testid={`reject-${row.id}`}
                      >
                        {t('payout.withdraw.reject.submit')}
                      </Button>
                    </>
                  ) : (
                    <span className="text-xs font-medium uppercase tracking-wider text-pw-text-muted">
                      {t(`payout.withdraw.status.${row.status}`)}
                    </span>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setDetailRow(row)}>
                    {t('payout.withdraw.view')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination — canonical /stores footer (Rows-per-page + Total +
          numbered), mirroring TopupQueuePage. Drives BE page/limit params. */}
      {rows.length > 0 ? (
        <PageListFooter
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          testId="withdraw-queue-footer"
        />
      ) : null}

      <ApproveDialog
        state={approveState}
        onClose={() => setApproveState(null)}
        onSubmitted={handleSubmitted}
      />
      <RejectDialog
        state={rejectState}
        onClose={() => setRejectState(null)}
        onSubmitted={handleSubmitted}
      />
      <DetailDrawer row={detailRow} onClose={() => setDetailRow(null)} />
    </main>
  );
}

export default WithdrawQueuePage;
